import { ChevronDown, Copy, HelpCircle, Pencil, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "react-aria-components";

import { Badge } from "@/components/badge";
import { Switch } from "@/components/switch";

import type { MBCReviewData } from "../types";
import type { SizeItem } from "./size-presets";

import { EditSizeModal } from "./EditSizeModal";
import { RemoveSizeDialog } from "./RemoveSizeDialog";
import { sizeIcon } from "./size-presets";

function CardShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-xl border border-border-primary bg-bg-primary p-5 ${className ?? ""}`}
    >
      {children}
    </section>
  );
}

function EditLink({ label, onPress }: { label: string; onPress?: () => void }) {
  return (
    <Button
      aria-label={`Edit ${label}`}
      className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-fg-brand-primary outline-hidden hover:underline"
      onPress={onPress}
    >
      <Pencil className="size-3.5" />
      Edit
    </Button>
  );
}

export function BrandGuidelinesCard({
  data,
}: {
  data: MBCReviewData["brandGuidelines"];
}) {
  return (
    <CardShell>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-text-primary">
          Brand guidelines
        </h3>
        <EditLink label="brand guidelines" />
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">Colors</span>
        <EditLink label="colors" />
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {data.colors.map((color, i) => (
          <div className="flex items-center gap-3" key={i}>
            <span
              className="size-9 shrink-0 rounded-lg border border-border-secondary"
              style={{ backgroundColor: color.swatch }}
            />
            <div className="flex flex-col">
              <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                {color.hex}
                <Copy className="size-3 text-fg-quaternary" />
              </span>
              <span className="text-xs text-text-tertiary">{color.name}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">
          Typography
        </span>
        <EditLink label="typography" />
      </div>
      <div className="mt-2 flex flex-col gap-1">
        {data.typography.map((font) => (
          <span className="text-sm text-text-secondary" key={font}>
            {font}
          </span>
        ))}
      </div>
    </CardShell>
  );
}

export function TechnicalDetailsCard({
  fileSizes,
  fileTypes,
  onSizesChange,
}: {
  fileSizes: MBCReviewData["brief"]["fileSizes"];
  fileTypes: MBCReviewData["brief"]["fileTypes"];
  /** Persist a new size list. When omitted the section is read-only. */
  onSizesChange?: (sizes: SizeItem[]) => void;
}) {
  const [noAiAssets, setNoAiAssets] = useState(true);
  const [sizes, setSizes] = useState<SizeItem[]>(fileSizes);
  const [editOpen, setEditOpen] = useState(false);
  const [removeIndex, setRemoveIndex] = useState<null | number>(null);

  const editable = Boolean(onSizesChange);

  // Keep local sizes in sync with the brief (re-seeds after a persisted edit).
  useEffect(() => {
    setSizes(fileSizes);
  }, [fileSizes]);

  const commit = (next: SizeItem[]) => {
    setSizes(next);
    onSizesChange?.(next);
  };

  return (
    <CardShell>
      <h3 className="text-lg font-semibold text-text-primary">
        Technical details
      </h3>

      <div className="mt-4 flex items-center gap-2">
        <Switch isSelected={noAiAssets} onChange={setNoAiAssets}>
          <span className="text-sm font-medium text-text-secondary">
            Do not use AI Assets
          </span>
        </Switch>
        <HelpCircle className="size-4 text-fg-quaternary" />
      </div>

      {/* Sizes needed */}
      <div className="mt-5 flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">
          Sizes needed <span className="text-text-error-primary">*</span>
        </span>
        {editable ? (
          <EditLink label="sizes needed" onPress={() => setEditOpen(true)} />
        ) : null}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2.5">
        {sizes.map((fs, i) => {
          const Icon = sizeIcon(fs.size);

          return (
            <div
              className="group relative flex flex-col gap-2 rounded-lg border border-border-primary bg-bg-primary p-3"
              key={`${fs.label}-${fs.size}-${i}`}
            >
              {editable ? (
                <button
                  aria-label={`Remove ${fs.label}`}
                  className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full text-fg-quaternary opacity-0 transition-opacity hover:text-text-error-primary group-hover:opacity-100"
                  onClick={() => setRemoveIndex(i)}
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
              <Icon className="size-4 text-fg-quaternary" />
              <div>
                <p className="text-sm font-semibold text-text-primary">
                  {fs.label}
                </p>
                <p className="text-xs text-text-tertiary">{fs.size}</p>
              </div>
            </div>
          );
        })}
      </div>

      {editable ? (
        <>
          <EditSizeModal
            isOpen={editOpen}
            onOpenChange={setEditOpen}
            onSave={commit}
            value={sizes}
          />
          <RemoveSizeDialog
            isOpen={removeIndex !== null}
            label={removeIndex === null ? undefined : sizes[removeIndex]?.label}
            onConfirm={() => {
              if (removeIndex !== null) {
                commit(sizes.filter((_, i) => i !== removeIndex));
              }
            }}
            onOpenChange={(open) => {
              if (!open) setRemoveIndex(null);
            }}
          />
        </>
      ) : null}

      {/* File types */}
      <div className="mt-5 flex items-center justify-between">
        <span className="text-sm font-semibold text-text-primary">
          File types <span className="text-text-error-primary">*</span>
        </span>
        <EditLink label="file types" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {fileTypes.map((type) => (
          <Badge isRounded key={type} size="medium" variant="purple">
            {type}
          </Badge>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text-secondary">
          Preferred designers
        </span>
        <FakeSelect placeholder="Select preferred designers" />
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <span className="flex items-center gap-1 text-sm font-medium text-text-secondary">
          Collaborators
          <HelpCircle className="size-3.5 text-fg-quaternary" />
        </span>
        <FakeSelect placeholder="Type first name to search..." />
      </div>
    </CardShell>
  );
}

function FakeSelect({ placeholder }: { placeholder: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border-primary bg-bg-primary px-3.5 py-2.5">
      <span className="flex-1 text-sm text-text-placeholder">
        {placeholder}
      </span>
      <ChevronDown className="size-4 text-fg-quaternary" />
    </div>
  );
}

export function BriefSummaryCard({
  data,
}: {
  data: MBCReviewData["briefSummary"];
}) {
  return (
    <CardShell>
      <h3 className="text-lg font-semibold text-text-primary">Brief summary</h3>
      <dl className="mt-4 flex flex-col gap-2.5">
        {data.map((row) => (
          <div className="flex gap-1.5 text-sm" key={row.label}>
            <dt className="text-text-secondary">{row.label}:</dt>
            <dd className="font-semibold text-text-primary">{row.value}</dd>
          </div>
        ))}
      </dl>
    </CardShell>
  );
}
